import { ThemeProvider } from '@mui/material';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from "react-query";
import ErrorBoundary from 'shared/ErrorBoundary';
import theme from 'shared/theme';
import { LicenseInfo } from "@mui/x-license-pro";
import { GlobalModals } from "./pages/store/GlobalModals";
import Layout from "shared/layout";
import {DynamicRouter} from "shared/DynamicRouter";
import {MainRoute} from "~/routes/MainRoute";
import {SearchRoute} from "~/routes/SearchRoute";
import {SignupRoute} from "~/routes/SignupRoute";
import {SignInRoute} from "~/routes/SignInRoute";
import {DetailPagesRoutes, UtilityRoutes} from "~/routes/DetailPageRoute";
import {useCallback, useEffect, useLayoutEffect} from "react";
import {useRouteStore} from "shared/store/RouteConfigStore";
import {useGlobalConfigStore} from "shared/store/GlobalConfigStore";
import {setup} from "shared/libs/axios";
import {useLocation} from "react-router-dom";
import React from "react";
import BreadCrumb from './components/BreadCrumb/BreadCrumb';

declare global {
  interface Window {
    __USP_SYNC_PORTAL_STYLES__?: (isBiz?: boolean) => void;
  }
}

LicenseInfo.setLicenseKey(`${process.env.REACT_APP_X_LICENSE_PRO}`);
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // refetchOnReconnect: false,
      // refetchOnMount: false,
      refetchOnWindowFocus: false,
      retryOnMount:false,
      retry: false,
      // onError: (err: any) =>{
      // }
    }
  }
});

const ErrorBoundaryWithRouteReset: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // 뒤로가기/앞으로가기처럼 라우팅만 바뀌는 경우에도,
  // 이전에 발생했던 런타임 에러 상태가 계속 남지 않도록 ErrorBoundary를 리셋합니다.
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

const PortalStyleSync: React.FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    window.__USP_SYNC_PORTAL_STYLES__?.(location.pathname.startsWith('/biz'));
  }, [location.pathname]);

  return null;
};

// 동적으로 <title> 변경 컴포넌트
const TitleManager = () => {
  const location = useLocation();
  const { pageDepths } = useRouteStore();

  useEffect(() => {
    let baseTitle = location.pathname.startsWith('/biz') ? '사업관리시스템' : 'AI 기업지원';
    let navTitle = '';
    // 뎁스 정보가 모두 없으면 메인
    if (!pageDepths?.depth1 && !pageDepths?.depth2 && !pageDepths?.depth3) {
      document.title = `인공지능산업융합사업단 - ${baseTitle}`;
      return;
    }
    if (pageDepths?.depth1 === '마이페이지' && (pageDepths?.depth2 === 'AI 기업지원' || pageDepths?.depth2 === '사업관리시스템')) {
      navTitle = [pageDepths.depth1, pageDepths.depth2, pageDepths.depth3]
        .filter(Boolean)
        .join(' > ');
    } else {
      navTitle = [pageDepths?.depth1, pageDepths?.depth2]
        .filter(Boolean)
        .join(' > ');
    }
    document.title = navTitle ? `${baseTitle} > ${navTitle}` : baseTitle;
  }, [location.pathname, pageDepths]);
  return null;
};

// react query config default ..
const App = () => {
  const routeStore = useRouteStore()
  const configStore = useGlobalConfigStore()

  const handlerResize = useCallback((e) => {
    configStore.setDevice(isMobileDevice()? 'mobile' : 'pc')
    const isDesktop = isMobileDevice()? window.screen.width> 768 : window.innerWidth > 768
    if (configStore.isDesktop != isDesktop)
      configStore.setScreenMode(isDesktop)
  }, [window.innerWidth])

  useEffect(() => {
    window.addEventListener("resize", handlerResize)
    return () => {
      window.removeEventListener("resize", handlerResize)
    }
  }, [handlerResize])

  useEffect(() => {
    routeStore.setLayout('empty')
    routeStore.setDefaultLayout('empty')
    if (process.env.REACT_APP_MODE == 'prd'){
      console.log = () => {}
      console.warn = () => {}
      console.error = () => {}
    }
  }, [])

  useEffect(() => {
    const domain = process.env.REACT_APP_DOMAIN
    if (domain) {
      setup({
        baseURL: `${window.location.protocol}//${domain.split('//')[1]}`
      })
    }
  }, [window.location.protocol])

  return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <TitleManager />
            <PortalStyleSync />
            <ErrorBoundaryWithRouteReset>
              <Layout>
                <DynamicRouter
                  portalType={routeStore.portal}
                  addRoutes={[
                    ...MainRoute,
                    ...SearchRoute,
                    ...SignupRoute,
                    ...SignInRoute,
                    ...UtilityRoutes,
                    ...DetailPagesRoutes,
                  ]}
                />
              </Layout>
              <GlobalModals />
            </ErrorBoundaryWithRouteReset>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
  );
}

export default App;
